import React from 'react';
import { Heart, Truck } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { ProductInCatalog } from '@/types/products';
import Image from 'next/image';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';

// Helper function to check if URL is a video
const isVideoUrl = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.includes(ext)) || lowerUrl.includes('/video/');
};

export interface ProductCardProps {
  product: ProductInCatalog;
  onToggleWishlist: (
    productId: string,
    name: string,
    price: number,
    images: string[],
  ) => void;
  priority?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onToggleWishlist,
  priority = false,
}) => {
  const router = useRouter(); 
  const { language } = useLanguage();
  const t = useTranslation(language); 

  // Stock status → color + label, mirroring the reference's 3-tier scheme
  const stockStatus = !product.available
    ? { color: '#b91c1c', label: 'Indisponibil' }
    : product.stock === undefined
      ? { color: 'var(--color-neutral-600)', label: 'Stoc indisponibil' }
      : product.stock === 0
        ? { color: '#b91c1c', label: 'Stoc epuizat' }
        : product.stock <= 5
          ? { color: 'var(--color-accent-700)', label: `Doar ${product.stock} bucăți rămase` }
          : { color: 'var(--color-accent-2-700)', label: `În stoc ${product.stock} bucăți` };

  return (
    <article className="card elev-sm" style={{ padding: 'var(--space-2)' }}>
      <div
        className="relative overflow-hidden"
        style={{ borderRadius: 'var(--radius-md)', aspectRatio: '1' }}
      >
        {isVideoUrl(product.images[0] || '') ? (
          <div className="relative w-full h-full bg-[var(--muted)]">
            <video
              src={product.images[0]}
              className="w-full h-full object-cover"
              muted
              playsInline
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <svg className="w-16 h-16 text-white opacity-80" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </div>
          </div>
        ) : (
          <Image
            src={product.images[0] || '/placeholder-product.jpg'}
            alt={product.name}
            className="washed w-full h-full object-cover"
            width={400}
            height={400}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            priority={priority}
          />
        )}

        {/* Status tags */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {product.isNew && <span className="tag tag-accent">Nou</span>}
          {product.isFeatured && <span className="tag tag-accent-2">Popular</span>}
          {product.flowerDetails?.sameDayDelivery && (
            <span className="tag tag-outline flex items-center gap-1" style={{ background: 'var(--color-bg)' }}>
              <Truck className="h-2.5 w-2.5" strokeWidth={2.75} /> Azi
            </span>
          )}
        </div>

        {/* Wishlist button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist(product.productId, product.name, product.price, product.images);
          }}
          aria-label={`Adaugă ${product.name} la favorite`}
          className="absolute top-2 right-2 flex items-center justify-center rounded-full cursor-pointer"
          style={{ width: 32, height: 32, border: 'none', background: 'color-mix(in srgb, var(--color-bg) 88%, transparent)' }}
        >
          <Heart className="h-4.5 w-4.5" strokeWidth={2.75} style={{ color: 'var(--color-accent)' }} />
        </button>
      </div>

      {/* Product details */}
      <div
        className="cursor-pointer"
        style={{ padding: 'var(--space-2) var(--space-2) var(--space-1)' }}
        onClick={() => router.push(`/shop/${product.productId}?slug=${product.slug}`)}
      >
        <p className="card-title" style={{ margin: '0 0 2px' }}>{product.name}</p>
        <p style={{ fontSize: 12, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)', margin: '0 0 var(--space-2)' }}>
          {product.category}
        </p>

        <div className="flex items-center justify-between" style={{ marginBottom: 'var(--space-2)' }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: 19 }}>
            {(product.price / 100).toFixed(0)} RON
          </span>
          {product.reviewCount > 0 ? (
            <span style={{ fontSize: 12.5, color: 'color-mix(in srgb, var(--color-text) 70%, transparent)' }}>
              ★ {product.rating?.toFixed(1) || '0.0'} ({product.reviewCount})
            </span>
          ) : (
            <span style={{ fontSize: 12.5, color: 'color-mix(in srgb, var(--color-text) 60%, transparent)' }}>
              {t('review.noReviewsShort')}
            </span>
          )}
        </div>

        {/* Stock indicator */}
        <p
          className="flex items-center gap-1.5"
          style={{ fontSize: 12.5, color: stockStatus.color, margin: '0 0 var(--space-2)' }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: stockStatus.color, display: 'inline-block' }} />
          {stockStatus.label}
        </p>

        <button
          onClick={() => router.push(`/shop/${product.productId}?slug=${product.slug}`)}
          className="btn btn-primary btn-block"
          style={{ marginTop: 0 }}
        >
          {t('product.viewDetails') || 'Vezi Detalii'}
        </button>
      </div>
    </article>
  );
};